/**
 * Admin Panorama Configuration Manager (Multi-Tour)
 * 
 * Manages hotspot/link configurations per tour using the backend server.
 */

async function getTourConfig(tourId) {
    try {
        const response = await fetch(`http://localhost:3002/api/tours/${tourId}`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();

        // Return both metadata and config
        if (data && data.config) {
            return data;
        }
    } catch (e) {
        console.warn(`Failed to fetch config for tour ${tourId}:`, e);
    }
    return null;
}

async function saveTourConfig(tourId, configArray) {
    try {
        const response = await fetch(`http://localhost:3002/api/tours/${tourId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(configArray),
        });
        return response.ok;
    } catch (e) {
        console.error(`Failed to save config to tour ${tourId}:`, e);
        return false;
    }
}

async function getSceneConfig(tourId, sceneIndex) {
    const data = await getTourConfig(tourId);
    if (!data || !data.config) return null;
    return data.config[sceneIndex] || null;
}

async function addInfoSpot(tourId, sceneIndex, infoSpot) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config) return null;

    const spot = {
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
        position: infoSpot.position,
        solutionModelData: {
            infoSpot: infoSpot.name || 'Untitled',
            infoSpotType: infoSpot.infoSpotType || 'audioTranscript',
            audioFolder: infoSpot.audioFolder || '',
            audioFile: infoSpot.audioFile || '',
            title: infoSpot.title || '',
            primaryContent: infoSpot.primaryContent || '',
            hotspotType: infoSpot.hotspotType || 'info',
            hotspotRoom: infoSpot.hotspotRoom || 'main',
            hotspotProduct: infoSpot.hotspotProduct || '',
            breadCrum: infoSpot.breadCrum || '',
            productHeadline: infoSpot.productHeadline || '',
            primaryImage: infoSpot.primaryImage || '',
            icon: infoSpot.icon || '',
            filterLabels: infoSpot.filterLabels || [],
        },
    };

    if (!data.config[sceneIndex]) return null;
    if (!data.config[sceneIndex].infoSpots) data.config[sceneIndex].infoSpots = [];

    data.config[sceneIndex].infoSpots.push(spot);
    await saveTourConfig(tourId, data.config);
    return spot;
}

async function updateInfoSpot(tourId, sceneIndex, spotId, updates) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config || !data.config[sceneIndex]) return false;

    const scene = data.config[sceneIndex];
    if (!scene.infoSpots) return false;

    const idx = scene.infoSpots.findIndex((s) => s.id === spotId);
    if (idx === -1) return false;

    if (updates.position) scene.infoSpots[idx].position = updates.position;
    if (updates.solutionModelData) {
        scene.infoSpots[idx].solutionModelData = {
            ...scene.infoSpots[idx].solutionModelData,
            ...updates.solutionModelData,
        };
    }
    if (updates.name) scene.infoSpots[idx].solutionModelData.infoSpot = updates.name;

    return await saveTourConfig(tourId, data.config);
}

async function deleteInfoSpot(tourId, sceneIndex, spotId) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config || !data.config[sceneIndex]) return false;

    if (!data.config[sceneIndex].infoSpots) return false;
    data.config[sceneIndex].infoSpots = data.config[sceneIndex].infoSpots.filter(
        (s) => s.id !== spotId
    );
    return await saveTourConfig(tourId, data.config);
}

async function addLink(tourId, sceneIndex, link) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config || !data.config[sceneIndex]) return null;

    // Use name from linked scene if none provided
    let linkName = link.name;
    if (!linkName && data.config[link.linkTo]) {
        linkName = data.config[link.linkTo].name;
    }

    const newLink = {
        id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
        name: linkName || 'Untitled',
        hotspotRoom: link.hotspotRoom || 'home_automation',
        linkTo: link.linkTo,
        position: link.position,
        scale: link.scale || { x: 3, y: 3, z: 1 },
        rotation: link.rotation || { x: 0, y: 0, z: 0 },
    };

    if (!data.config[sceneIndex].links) data.config[sceneIndex].links = [];
    data.config[sceneIndex].links.push(newLink);
    await saveTourConfig(tourId, data.config);
    return newLink;
}

async function updateLink(tourId, sceneIndex, linkId, updates) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config || !data.config[sceneIndex]) return false;

    const scene = data.config[sceneIndex];
    if (!scene.links) return false;

    const idx = scene.links.findIndex((l) => l.id === linkId);
    if (idx === -1) return false;

    scene.links[idx] = { ...scene.links[idx], ...updates };
    return await saveTourConfig(tourId, data.config);
}

async function deleteLink(tourId, sceneIndex, linkId) {
    let data = await getTourConfig(tourId);
    if (!data || !data.config || !data.config[sceneIndex]) return false;

    if (!data.config[sceneIndex].links) return false;
    data.config[sceneIndex].links = data.config[sceneIndex].links.filter(
        (l) => l.id !== linkId
    );
    return await saveTourConfig(tourId, data.config);
}

export {
    getTourConfig,
    saveTourConfig,
    getSceneConfig,
    addInfoSpot,
    updateInfoSpot,
    deleteInfoSpot,
    addLink,
    updateLink,
    deleteLink
};
